import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
 DndContext,
 closestCenter,
 KeyboardSensor,
 PointerSensor,
 useSensor,
 useSensors,
} from '@dnd-kit/core';
import {
 arrayMove,
 SortableContext,
 sortableKeyboardCoordinates,
 verticalListSortingStrategy,
 useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
 RefreshCw, DollarSign, Tag, ClipboardList,
 Plus, Trash2, AlertCircle, Check, Pencil, X, Ban,
 GripVertical, PackageOpen, Ticket, HelpCircle, CheckCircle2,
 Lock, Zap, Dumbbell, Utensils, LayoutGrid, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { authClient } from '../lib/authClient';
import { Profiles } from '../services/employeeApi';
import { ManagerApi } from '../services/managerApi';
import AssetImageField from '../components/AssetImageField';
import { isTourDemoActive } from '../services/tourDemo';

const TABS = ['pricing', 'promos', 'onboarding'];
const PRODUCT_DESCRIPTION_MAX = 280;
const QUESTION_DESCRIPTION_MAX = 300;
const CHOICE_STEP_TYPES = ['select', 'multiselect'];

const isChoiceStep = (type) => CHOICE_STEP_TYPES.includes(type);
const isCodeActive = (entry) => entry?.active !== false;

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel, variant = 'danger' }) {
 const confirmBtnRef = useRef(null);
 const isDanger = variant === 'danger';

 useEffect(() => {
 confirmBtnRef.current?.focus();
 const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
 document.addEventListener('keydown', handleKey);
 return () => document.removeEventListener('keydown', handleKey);
 }, [onCancel]);

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="absolute inset-0 bg-black/60 "
 onClick={onCancel}
 />
 <motion.div
 initial={{ opacity: 0, scale: 0.92, y: 14 }}
 animate={{ opacity: 1, scale: 1, y: 0 }}
 exit={{ opacity: 0, scale: 0.92, y: 14 }}
 transition={{ type: 'spring', damping: 22, stiffness: 320 }}
 className="relative z-10 w-full max-w-sm rounded-lg bg-surface dark:bg-surface-dark overflow-hidden border border-line dark:border-line-dark"
 >
 <div className={`h-0.5 ${isDanger ? ' bg-red-500 ' : ' bg-amber-500 '}`} />
 <div className="p-6">
 <div className="flex items-start gap-4 mb-6">
 <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ring-4 ${
 isDanger
 ? 'bg-red-100 dark:bg-red-500/20 ring-red-50 dark:ring-red-500/10'
 : 'bg-amber-100 dark:bg-amber-500/20 ring-amber-50 dark:ring-amber-500/10'
 }`}>
 {isDanger
 ? <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
 : <Ban className="w-5 h-5 text-amber-600 dark:text-amber-400" />
 }
 </div>
 <div className="flex-1 min-w-0">
 {title && (
 <h3 className="text-sm font-semibold text-ink dark:text-white mb-1.5">
 {title}
 </h3>
 )}
 <p className="text-sm text-ink-muted dark:text-ink-muted-dark leading-relaxed">{message}</p>
 </div>
 </div>
 <div className="flex gap-2.5">
 <button
 type="button"
 onClick={onCancel}
 className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium border border-line dark:border-line-dark bg-transparent text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-surface-dark/50 transition-all"
 >
 {cancelLabel}
 </button>
 <button
 ref={confirmBtnRef}
 type="button"
 onClick={onConfirm}
 className={`flex-1 px-4 py-2.5 rounded-md text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all active:scale-95 ${
 isDanger
 ? 'bg-red-600 hover:bg-red-700 '
 : 'bg-amber-600 hover:bg-amber-700 '
 }`}
 >
 {isDanger ? <Trash2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
 {confirmLabel}
 </button>
 </div>
 </div>
 </motion.div>
 </div>
 );
}

// ─── Side Drawer ───────────────────────────────────────────────────────────────
function Drawer({ open, onClose, title, children, isDarkMode, isRTL, dataTour }) {
 useEffect(() => {
 if (!open) return;
 const handleKey = (e) => {
 // Tour owns Escape while the guided playbook is open.
 if (isTourDemoActive()) return;
 if (e.key === 'Escape') onClose();
 };
 document.addEventListener('keydown', handleKey);
 document.body.style.overflow = 'hidden';
 return () => {
 document.removeEventListener('keydown', handleKey);
 document.body.style.overflow = '';
 };
 }, [open, onClose]);

 return (
 <AnimatePresence>
 {open && (
 <div className="fixed inset-0 z-40">
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.2 }}
 className="absolute inset-0 z-0 bg-black/50 "
 onClick={onClose}
 />
 <motion.div
 data-tour={dataTour}
 initial={{ x: isRTL ? '-100%' : '100%' }}
 animate={{ x: 0 }}
 exit={{ x: isRTL ? '-100%' : '100%' }}
 transition={{ type: 'spring', damping: 28, stiffness: 220, mass: 0.9 }}
 onClick={(e) => e.stopPropagation()}
 className={`absolute top-0 z-10 ${isRTL ? 'left-0 border-r' : 'right-0 border-l'} h-full w-full max-w-lg flex flex-col ${
 isDarkMode
 ? 'bg-canvas/95  border-line'
 : 'bg-surface  border-line'
 }`}
 role="dialog"
 aria-modal="true"
 >
 <div className={`flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b flex-shrink-0 ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
 <h2 className={`text-base font-semibold tracking-tight ${isDarkMode ? 'text-white' : 'text-ink'}`}>
 {title}
 </h2>
 <button
 type="button"
 onClick={onClose}
 className={`w-10 h-10 flex items-center justify-center rounded-md transition-all ${
 isDarkMode ? 'text-ink-muted hover:text-white hover:bg-line-dark/80' : 'text-ink-muted hover:text-ink hover:bg-surface'
 }`}
 aria-label="Close"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 sm:py-6">
 {children}
 </div>
 </motion.div>
 </div>
 )}
 </AnimatePresence>
 );
}

// ─── Empty State ───────────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, subtitle, ctaLabel, onCta, isDarkMode }) {
 return (
 <motion.div
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.3 }}
 className="flex flex-col items-center justify-center py-14 text-center select-none"
 >
 <div className="relative mb-6">
 <div className={`w-20 h-20 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
 <Icon className={`w-9 h-9 ${isDarkMode ? 'text-ink-muted' : 'text-ink-dark'}`} />
 </div>
 <div className={`absolute inset-0 rounded-lg blur-2xl scale-125 -z-10 ${isDarkMode ? 'bg-forest/15' : 'bg-forest/60'}`} />
 </div>
 <p className={`text-sm font-semibold mb-1.5 ${isDarkMode ? 'text-ink-dark' : 'text-ink-muted'}`}>{title}</p>
 {subtitle && (
 <p className={`text-xs max-w-[210px] leading-relaxed ${isDarkMode ? 'text-ink-muted' : 'text-ink-muted'}`}>{subtitle}</p>
 )}
 {ctaLabel && onCta && (
 <motion.button
 whileHover={{ scale: 1.04 }}
 whileTap={{ scale: 0.96 }}
 type="button"
 onClick={onCta}
 className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-forest text-white hover:bg-forest-hover transition-colors"
 >
 <Plus className="w-4 h-4" />
 {ctaLabel}
 </motion.button>
 )}
 </motion.div>
 );
}

// ─── Sortable Question Card ────────────────────────────────────────────────────
function SortableQuestion({
 step, idx,
 onUpdate, onRemove,
 onAddOption, onRemoveOption,
 optionDraft, onOptionDraftChange,
 t, themeClasses, isDarkMode,
}) {
 const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });

 const style = { transform: CSS.Transform.toString(transform), transition };

 const stepTypeLabel = (type) => {
 if (type === 'text') return t('onboarding.typeText');
 if (type === 'multiselect') return t('onboarding.typeMultiselect');
 return t('onboarding.typeSelect');
 };

 return (
 <motion.div
 ref={setNodeRef}
 style={style}
 layout
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: isDragging ? 0.55 : 1, y: 0, scale: isDragging ? 1.02 : 1 }}
 exit={{ opacity: 0, height: 0, marginBottom: 0 }}
 transition={{ layout: { type: 'spring', damping: 20, stiffness: 200 } }}
 className={`rounded-lg border overflow-hidden ${
 isDragging
 ? ' ring-2 ring-forest/40 z-10'
 : isDarkMode ? '' : ''
 } ${isDarkMode ? 'bg-surface/70 border-line' : 'bg-surface border-line'}`}
 >
 <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
 <div className="flex items-center gap-2.5">
 <button
 type="button"
 {...attributes}
 {...listeners}
 aria-label={t('onboarding.reorder')}
 className={`p-2.5 -m-1 rounded-lg cursor-grab active:cursor-grabbing touch-none select-none transition-colors ${
 isDarkMode ? 'text-ink-muted hover:text-ink-dark hover:bg-line-dark' : 'text-ink-muted hover:text-ink-muted hover:bg-surface'
 }`}
 >
 <GripVertical className="w-4 h-4" />
 </button>
 <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${isDarkMode ? 'bg-forest-faint0/15 text-forest-ondark' : 'bg-forest-faint text-forest-ondark'}`}>
 #{idx + 1}
 </span>
 </div>
 <button
 type="button"
 onClick={() => onRemove(step.id)}
 aria-label={t('onboarding.remove')}
 className={`p-2.5 -m-1 rounded-md transition-all ${isDarkMode ? 'text-ink-muted hover:text-red-400 hover:bg-red-500/10' : 'text-ink-muted hover:text-red-500 hover:bg-red-50'}`}
 >
 <X className="w-4 h-4" />
 </button>
 </div>

 <div className="p-4 space-y-4">
 <div className={`flex gap-0.5 p-1 rounded-md ${isDarkMode ? 'bg-canvas/60' : 'bg-surface'}`}>
 {['text', 'select', 'multiselect'].map((type) => (
 <button
 key={type}
 type="button"
 onClick={() => onUpdate(step.id, { type, options: isChoiceStep(type) ? (step.options || []) : [] })}
 className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
 step.type === type
 ? 'bg-forest text-white '
 : isDarkMode ? 'text-ink-muted hover:text-ink-dark' : 'text-ink-muted hover:text-ink'
 }`}
 >
 {stepTypeLabel(type)}
 </button>
 ))}
 </div>

 <div>
 <label className={`block text-xs font-medium mb-1.5 ${themeClasses.textSecondary}`}>{t('onboarding.question')}</label>
 <input
 className={themeClasses.inputField}
 value={step.question}
 onChange={(e) => onUpdate(step.id, { question: e.target.value })}
 placeholder={t('onboarding.questionPlaceholder')}
 />
 </div>

 <div>
 <label className={`block text-xs font-medium mb-1.5 ${themeClasses.textSecondary}`}>{t('onboarding.description')}</label>
 <textarea
 className={`${themeClasses.inputField} min-h-[68px] resize-y`}
 value={step.description || ''}
 maxLength={QUESTION_DESCRIPTION_MAX}
 onChange={(e) => onUpdate(step.id, { description: e.target.value })}
 placeholder={t('onboarding.descriptionHint')}
 />
 <p className={`text-[11px] mt-0.5 text-end ${themeClasses.textMuted}`}>
 {(step.description || '').length}/{QUESTION_DESCRIPTION_MAX}
 </p>
 </div>

 {isChoiceStep(step.type) && (
 <div>
 <label className={`block text-xs font-medium mb-2 ${themeClasses.textSecondary}`}>{t('onboarding.options')}</label>
 <div className="flex gap-2 mb-1.5">
 <input
 className={`${themeClasses.inputField} flex-1`}
 value={optionDraft || ''}
 onChange={(e) => onOptionDraftChange(step.id, e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter') { e.preventDefault(); onAddOption(step.id); }
 }}
 placeholder={t('onboarding.optionPlaceholder')}
 />
 <button
 type="button"
 onClick={() => onAddOption(step.id)}
 className={`px-3.5 py-2.5 rounded-md text-xs font-semibold border shrink-0 flex items-center gap-1.5 transition-all ${
 isDarkMode ? 'border-line bg-surface text-ink hover:bg-line-dark' : 'border-line bg-surface text-ink hover:bg-canvas'
 }`}
 >
 <Plus className="w-3.5 h-3.5" />
 {t('onboarding.addOption')}
 </button>
 </div>
 <p className={`text-[11px] mb-2.5 ${themeClasses.textMuted}`}>{t('onboarding.pressEnter')}</p>
 <AnimatePresence>
 {(step.options || []).length === 0 ? (
 <p className={`text-xs italic ${themeClasses.textMuted}`}>{t('onboarding.noOptions')}</p>
 ) : (
 <div className="flex flex-wrap gap-2">
 {(step.options || []).map((opt, optIdx) => (
 <motion.span
 key={`${step.id}-${optIdx}-${opt}`}
 initial={{ scale: 0.8, opacity: 0 }}
 animate={{ scale: 1, opacity: 1 }}
 exit={{ scale: 0.8, opacity: 0 }}
 className={`inline-flex items-center gap-1.5 ps-3 pe-1 py-1 rounded-full text-xs font-medium border ${
 isDarkMode ? 'bg-line-dark/80 border-line text-ink' : 'bg-surface border-line text-ink'
 }`}
 >
 {opt}
 <button
 type="button"
 onClick={() => onRemoveOption(step.id, optIdx)}
 aria-label={`${t('onboarding.remove')}: ${opt}`}
 className={`p-1.5 rounded-full transition-colors ${
 isDarkMode ? 'text-ink-muted hover:text-red-400 hover:bg-red-500/15' : 'text-ink-muted hover:text-red-500 hover:bg-red-50'
 }`}
 >
 <X className="w-3.5 h-3.5" />
 </button>
 </motion.span>
 ))}
 </div>
 )}
 </AnimatePresence>
 </div>
 )}
 </div>
 </motion.div>
 );
}

// ─── Main Component ────────────────────────────────────────────────────────────
function ManagerDashboardPage() {
 const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();

 const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
 const direction = language === 'hebrew' ? 'rtl' : 'ltr';
 const isRTL = direction === 'rtl';

 useEffect(() => {
 const handleStorageChange = () => setLanguage(localStorage.getItem('language') || 'english');
 window.addEventListener('storage', handleStorageChange);
 window.addEventListener('languageChange', handleStorageChange);
 document.documentElement.dir = direction;
 document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
 return () => {
 window.removeEventListener('storage', handleStorageChange);
 window.removeEventListener('languageChange', handleStorageChange);
 };
 }, [direction, language]);

 const themeClasses = {
 ...baseThemeClasses,
 inputField: `w-full rounded-md border px-4 py-2.5 text-sm outline-none transition-all focus:border-forest focus:ring-2 focus:ring-forest ${isDarkMode ? 'bg-canvas/60 border-line text-ink placeholder-ink-muted' : 'bg-surface border-line text-ink placeholder-ink-muted'}`,
 select: `w-full rounded-md border px-4 py-2.5 text-sm outline-none transition-all focus:border-forest focus:ring-2 focus:ring-forest ${isDarkMode ? 'bg-canvas/60 border-line text-ink' : 'bg-surface border-line text-ink'}`,
 btnPrimary: 'px-5 py-2.5 rounded-md font-semibold text-sm bg-forest text-white hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]',
 btnSecondary: `px-4 py-2.5 rounded-md font-medium text-sm border flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98] ${isDarkMode ? 'border-line bg-surface/70 text-ink hover:bg-line-dark' : 'border-line bg-surface text-ink hover:bg-canvas'} `,
 };

 const cardBase = isDarkMode
 ? 'bg-surface-dark border border-line rounded-lg p-6'
 : 'bg-surface border border-line rounded-lg p-6';

 const translations = {
 english: {
 'page.title': 'Pricing & Signup',
 'page.subtitle': 'Configure subscription plans, promo codes, and custom client signup questions',
 'access.restricted': 'Access restricted',
 'access.message': 'Only company admins can configure pricing and signup settings.',
 'loading': 'Loading…',
 'refresh': 'Refresh',
 'error': 'Error',
 'tab.pricing': 'Pricing',
 'tab.promos': 'Promo Codes',
 'tab.onboarding': 'Onboarding',
 'pricing.title': 'Custom Products',
 'pricing.planName': 'Plan name',
 'pricing.price': 'Price (USD)',
 'pricing.interval': 'Billing cycle',
 'pricing.month': 'Monthly',
 'pricing.year': 'Yearly',
 'pricing.description': 'Description (optional)',
 'pricing.descriptionHint': 'Shown to clients when choosing a plan',
 'pricing.productLogo': 'Plan logo',
 'pricing.productLogoHint': 'Upload a square logo or icon — shown on the plan selection screen',
 'pricing.category': 'Category',
 'pricing.categoryNutrition': 'Nutrition',
 'pricing.categoryTraining': 'Training',
 'pricing.categoryOther': 'Other',
 'pricing.create': 'New product',
 'pricing.update': 'Save changes',
 'pricing.cancelEdit': 'Cancel',
 'pricing.edit': 'Edit',
 'pricing.delete': 'Delete',
 'pricing.deleteConfirm': 'Delete this product? It will be removed from your company and deactivated in Stripe.',
 'pricing.editing': 'Editing product',
 'pricing.empty': 'No custom products yet.',
 'pricing.emptySubtitle': 'Create subscription plans that your clients will choose from during signup.',
 'pricing.stripeId': 'Stripe ID',
 'pricing.scrollMore': 'More columns — scroll sideways',
 'promos.discountTitle': 'Percentage discount',
 'promos.couponName': 'Coupon name (Stripe)',
 'promos.couponNameHint': 'Shown in Stripe dashboard, e.g. "BetterChoice Coupon"',
 'promos.percentOff': 'Percent off',
 'promos.code': 'Promo code',
 'promos.products': 'Applicable products',
 'promos.productsHint': 'Choose which plans this discount applies to',
 'promos.productsRequired': 'Select at least one product for this coupon',
 'promos.selectAll': 'Select all',
 'promos.selectNone': 'Clear',
 'promos.maxRedemptions': 'Max uses',
 'promos.maxRedemptionsHint': 'Leave empty for unlimited',
 'promos.unlimited': 'Unlimited',
 'promos.uses': 'uses',
 'promos.createDiscount': 'Create discount code',
 'promos.editing': 'Editing Stripe coupon',
 'promos.editHint': 'Percent off, promo code text, and products cannot be changed after creation in Stripe. You can update the coupon name and max uses.',
 'promos.edit': 'Edit',
 'promos.update': 'Save changes',
 'promos.cancelEdit': 'Cancel',
 'promos.readOnly': 'Locked after creation',
 'promos.bypassStripeNote': 'Bypass codes are not created in Stripe. They are stored in your company settings and validated by your app during signup to grant 100% free access without payment.',
 'promos.bypassWhere': 'You will see bypass codes in the Active codes list below (tagged App only), not in the Stripe dashboard.',
 'promos.stripeTag': 'Stripe',
 'promos.appOnlyTag': 'App only',
 'promos.bypassTitle': '100% free bypass code',
 'promos.bypassCode': 'Bypass code',
 'promos.expiresAt': 'Expires at (optional)',
 'promos.createBypass': 'Create bypass code',
 'promos.listTitle': 'Active codes',
 'promos.empty': 'No promo or bypass codes yet.',
 'promos.emptySubtitle': 'Create discount codes or free-access bypass codes for your clients.',
 'promos.inactiveTag': 'Inactive',
 'promos.deactivate': 'Deactivate',
 'promos.deleteCode': 'Delete',
 'promos.deactivatePromoConfirm': 'Deactivate this promo code? It will stop working in Stripe but stay in your list.',
 'promos.deletePromoConfirm': 'Delete this promo code? It will be removed from your list and deactivated in Stripe.',
 'promos.deactivateBypassConfirm': 'Deactivate this bypass code? Clients will no longer be able to use it.',
 'promos.deleteBypassConfirm': 'Delete this bypass code? It will be removed permanently.',
 'onboarding.title': 'Custom questions',
 'onboarding.add': 'Add question',
 'onboarding.reorder': 'Drag to reorder',
 'onboarding.remove': 'Remove',
 'onboarding.type': 'Type',
 'onboarding.typeText': 'Text',
 'onboarding.typeSelect': 'Single choice',
 'onboarding.typeMultiselect': 'Multiple choice',
 'onboarding.question': 'Question',
 'onboarding.description': 'Description (optional)',
 'onboarding.descriptionHint': 'Extra context shown below the question',
 'onboarding.questionPlaceholder': 'e.g. What is your main health goal?',
 'onboarding.options': 'Answer choices',
 'onboarding.optionPlaceholder': 'Type an option and press Add',
 'onboarding.addOption': 'Add',
 'onboarding.noOptions': 'No choices yet — add at least one',
 'onboarding.pressEnter': 'Press Enter to add',
 'onboarding.save': 'Save questions',
 'onboarding.empty': 'No custom questions yet.',
 'onboarding.emptySubtitle': 'Personalise your client onboarding by adding questions they answer during signup.',
 'success.saved': 'Saved successfully',
 },
 hebrew: {
 'page.title': 'תמחור והרשמה',
 'page.subtitle': 'הגדר תוכניות מנוי, קודי הנחה ושאלות הרשמה מותאמות ללקוחות',
 'access.restricted': 'הגישה מוגבלת',
 'access.message': 'רק מנהלי חברה יכולים להגדיר תמחור והגדרות הרשמה.',
 'loading': 'טוען…',
 'refresh': 'רענן',
 'error': 'שגיאה',
 'tab.pricing': 'תמחור',
 'tab.promos': 'קודי הנחה',
 'tab.onboarding': 'הצטרפות',
 'pricing.title': 'מוצרים מותאמים',
 'pricing.planName': 'שם התוכנית',
 'pricing.price': 'מחיר (USD)',
 'pricing.interval': 'מחזור חיוב',
 'pricing.month': 'חודשי',
 'pricing.year': 'שנתי',
 'pricing.description': 'תיאור (אופציונלי)',
 'pricing.descriptionHint': 'מוצג ללקוחות בעת בחירת תוכנית',
 'pricing.productLogo': 'לוגו התוכנית',
 'pricing.productLogoHint': 'העלה לוגו או אייקון מרובע — יוצג במסך בחירת התוכנית',
 'pricing.category': 'קטגוריה',
 'pricing.categoryNutrition': 'תזונה',
 'pricing.categoryTraining': 'אימונים',
 'pricing.categoryOther': 'אחר',
 'pricing.create': 'מוצר חדש',
 'pricing.update': 'שמור שינויים',
 'pricing.cancelEdit': 'ביטול',
 'pricing.edit': 'ערוך',
 'pricing.delete': 'מחק',
 'pricing.deleteConfirm': 'למחוק מוצר זה? הוא יוסר מהחברה ויושבת ב-Stripe.',
 'pricing.editing': 'עריכת מוצר',
 'pricing.empty': 'אין מוצרים מותאמים עדיין.',
 'pricing.emptySubtitle': 'צור תוכניות מנוי שלקוחותיך יוכלו לבחור מהן בעת ההרשמה.',
 'pricing.stripeId': 'מזהה Stripe',
 'pricing.scrollMore': 'עוד עמודות — גלול לצד',
 'promos.discountTitle': 'הנחה באחוזים',
 'promos.couponName': 'שם הקופון (Stripe)',
 'promos.couponNameHint': 'מוצג בלוח Stripe, למשל "BetterChoice Coupon"',
 'promos.percentOff': 'אחוז הנחה',
 'promos.code': 'קוד קופון',
 'promos.products': 'מוצרים חלים',
 'promos.productsHint': 'בחר לאילו תוכניות ההנחה חלה',
 'promos.productsRequired': 'בחר לפחות מוצר אחד לקופון',
 'promos.selectAll': 'בחר הכל',
 'promos.selectNone': 'נקה',
 'promos.maxRedemptions': 'מקסימום שימושים',
 'promos.maxRedemptionsHint': 'השאר ריק ללא הגבלה',
 'promos.unlimited': 'ללא הגבלה',
 'promos.uses': 'שימושים',
 'promos.createDiscount': 'צור קוד הנחה',
 'promos.editing': 'עריכת קופון Stripe',
 'promos.editHint': 'אחוז ההנחה, טקסט הקוד והמוצרים לא ניתנים לשינוי ב-Stripe אחרי היצירה. ניתן לעדכן שם קופון ומקסימום שימושים.',
 'promos.edit': 'ערוך',
 'promos.update': 'שמור שינויים',
 'promos.cancelEdit': 'ביטול',
 'promos.readOnly': 'נעול אחרי יצירה',
 'promos.bypassStripeNote': 'קודי עקיפה לא נוצרים ב-Stripe. הם נשמרים בהגדרות החברה ומאומתים באפליקציה בעת ההרשמה כדי לתת גישה חינמית ללא תשלום.',
 'promos.bypassWhere': 'קודי עקיפה מופיעים ברשימת הקודים הפעילים למטה (תגית אפליקציה בלבד), לא בלוח Stripe.',
 'promos.stripeTag': 'Stripe',
 'promos.appOnlyTag': 'אפליקציה בלבד',
 'promos.bypassTitle': 'קוד עקיפה חינמי 100%',
 'promos.bypassCode': 'קוד עקיפה',
 'promos.expiresAt': 'תוקף (אופציונלי)',
 'promos.createBypass': 'צור קוד עקיפה',
 'promos.listTitle': 'קודים פעילים',
 'promos.empty': 'אין קודי הנחה או עקיפה עדיין.',
 'promos.emptySubtitle': 'צור קודי הנחה או קודי גישה חופשית עבור לקוחותיך.',
 'promos.inactiveTag': 'לא פעיל',
 'promos.deactivate': 'השבת',
 'promos.deleteCode': 'מחק',
 'promos.deactivatePromoConfirm': 'להשבית קוד הנחה זה? הוא יפסיק לעבוד ב-Stripe אך יישאר ברשימה.',
 'promos.deletePromoConfirm': 'למחוק קוד הנחה זה? הוא יוסר מהרשימה ויושבת ב-Stripe.',
 'promos.deactivateBypassConfirm': 'להשבית קוד עקיפה זה? לקוחות לא יוכלו להשתמש בו.',
 'promos.deleteBypassConfirm': 'למחוק קוד עקיפה זה? הוא יוסר לצמיתות.',
 'onboarding.title': 'שאלות מותאמות',
 'onboarding.add': 'הוסף שאלה',
 'onboarding.reorder': 'גררו לשינוי הסדר',
 'onboarding.remove': 'הסרה',
 'onboarding.type': 'סוג',
 'onboarding.typeText': 'טקסט',
 'onboarding.typeSelect': 'בחירה יחידה',
 'onboarding.typeMultiselect': 'בחירה מרובה',
 'onboarding.question': 'שאלה',
 'onboarding.description': 'תיאור (אופציונלי)',
 'onboarding.descriptionHint': 'הקשר נוסף שמוצג מתחת לשאלה',
 'onboarding.questionPlaceholder': 'למשל: מה המטרה העיקרית שלך?',
 'onboarding.options': 'אפשרויות תשובה',
 'onboarding.optionPlaceholder': 'הקלד אפשרות ולחץ הוסף',
 'onboarding.addOption': 'הוסף',
 'onboarding.noOptions': 'אין אפשרויות עדיין — הוסף לפחות אחת',
 'onboarding.pressEnter': 'Enter להוספה',
 'onboarding.save': 'שמור שאלות',
 'onboarding.empty': 'אין שאלות מותאמות עדיין.',
 'onboarding.emptySubtitle': 'התאם אישית את תהליך הצטרפות הלקוחות שלך על ידי הוספת שאלות.',
 'success.saved': 'נשמר בהצלחה',
 },
 };

 const t = (key) => translations[language]?.[key] ?? translations.english[key] ?? key;

 // ── Core state ──────────────────────────────────────────────────────────────
 const [loading, setLoading] = useState(true);
 const [isAuthorized, setIsAuthorized] = useState(false);
 const [error, setError] = useState(null);
 const [activeTab, setActiveTab] = useState('pricing');
 const [refreshing, setRefreshing] = useState(false);

 const [companyName, setCompanyName] = useState('');
 const [customProducts, setCustomProducts] = useState([]);
 const [promoCodes, setPromoCodes] = useState([]);
 const [bypassCodes, setBypassCodes] = useState([]);
 const [customSteps, setCustomSteps] = useState([]);
 const [optionDrafts, setOptionDrafts] = useState({});

 const [pricingForm, setPricingForm] = useState({ planName: '', price: '', interval: 'month', description: '', category: 'other', logoUrl: '' });
 const [pricingTouched, setPricingTouched] = useState({ planName: false, price: false });
 const [editingProductId, setEditingProductId] = useState(null);
 const [editingPromoId, setEditingPromoId] = useState(null);
 const [deletingProductId, setDeletingProductId] = useState(null);
 const [deactivatingPromoId, setDeactivatingPromoId] = useState(null);
 const [deletingPromoId, setDeletingPromoId] = useState(null);
 const [deactivatingBypassId, setDeactivatingBypassId] = useState(null);
 const [deletingBypassId, setDeletingBypassId] = useState(null);
 const [discountForm, setDiscountForm] = useState({
 couponName: '',
 percentageOff: '',
 promoCodeText: '',
 selectedProductIds: [],
 maxRedemptions: '',
 });
 const [bypassForm, setBypassForm] = useState({ code: '', expiresAt: '' });
 const [savingPricing, setSavingPricing] = useState(false);
 const [savingDiscount, setSavingDiscount] = useState(false);
 const [savingBypass, setSavingBypass] = useState(false);
 const [savingOnboarding, setSavingOnboarding] = useState(false);

 // ── UI state ────────────────────────────────────────────────────────────────
 const [confirmState, setConfirmState] = useState({
 open: false, message: '', title: '', confirmLabel: '', cancelLabel: '', onConfirm: null, variant: 'danger',
 });
 const [pricingDrawerOpen, setPricingDrawerOpen] = useState(false);
 const [promoDrawerOpen, setPromoDrawerOpen] = useState(false);
 const [bypassDrawerOpen, setBypassDrawerOpen] = useState(false);

 // ── DnD sensors ─────────────────────────────────────────────────────────────
 const sensors = useSensors(
 useSensor(PointerSensor),
 useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
 );

 // ── Confirm modal helpers ────────────────────────────────────────────────────
 const triggerConfirm = useCallback((opts) => {
 setConfirmState({ open: true, message: '', title: '', confirmLabel: '', cancelLabel: t('pricing.cancelEdit'), onConfirm: null, variant: 'danger', ...opts });
 }, []);

 const closeConfirm = useCallback(() => {
 setConfirmState((s) => ({ ...s, open: false }));
 }, []);

 const executeConfirm = useCallback(() => {
 const fn = confirmState.onConfirm;
 closeConfirm();
 fn?.();
 }, [confirmState.onConfirm, closeConfirm]);

 // Guided tour: open/close the New product drawer on Pricing.
 useEffect(() => {
 const openPricing = () => {
 setActiveTab('pricing');
 setEditingProductId(null);
 setPricingForm({ planName: '', price: '', interval: 'month', description: '', category: 'other', logoUrl: '' });
 setPricingTouched({ planName: false, price: false });
 setPricingDrawerOpen(true);
 };
 const closePricing = () => {
 setPricingDrawerOpen(false);
 setEditingProductId(null);
 setPricingForm({ planName: '', price: '', interval: 'month', description: '', category: 'other', logoUrl: '' });
 setPricingTouched({ planName: false, price: false });
 };
 const activatePricingTab = () => {
 setActiveTab('pricing');
 setPricingDrawerOpen(false);
 };
 window.addEventListener('portfolio-playbook-open-pricing-drawer', openPricing);
 window.addEventListener('portfolio-playbook-close-pricing-drawer', closePricing);
 window.addEventListener('portfolio-playbook-activate-pricing-tab', activatePricingTab);
 return () => {
 window.removeEventListener('portfolio-playbook-open-pricing-drawer', openPricing);
 window.removeEventListener('portfolio-playbook-close-pricing-drawer', closePricing);
 window.removeEventListener('portfolio-playbook-activate-pricing-tab', activatePricingTab);
 };
 }, []);

 // ── Drawer close helpers ─────────────────────────────────────────────────────
 const closePricingDrawer = useCallback(() => {
 setPricingDrawerOpen(false);
 setPricingForm({ planName: '', price: '', interval: 'month', description: '', category: 'other', logoUrl: '' });
 setEditingProductId(null);
 setPricingTouched({ planName: false, price: false });
 }, []);

 const closePromoDrawer = useCallback(() => {
 setPromoDrawerOpen(false);
 setEditingPromoId(null);
 setDiscountForm((prev) => ({
 ...prev,
 percentageOff: '',
 promoCodeText: '',
 maxRedemptions: '',
 }));
 }, []);

 // ── Data helpers ─────────────────────────────────────────────────────────────
 const applyConfig = (data) => {
 const name = data.name || '';
 setCompanyName(name);
 const products = data.config?.pricing?.customProducts || [];
 setCustomProducts(products);
 setPromoCodes(data.config?.pricing?.promoCodes || []);
 setBypassCodes(data.config?.pricing?.bypassCodes || []);
 setCustomSteps(
 (data.config?.onboarding?.customSteps || []).map((s) => ({
 ...s,
 options: Array.isArray(s.options)
 ? s.options
 : typeof s.options === 'string'
 ? s.options.split(/[,;\n]/).map((o) => o.trim()).filter(Boolean)
 : [],
 }))
 );
 setDiscountForm((prev) => ({
 ...prev,
 couponName: prev.couponName || (name ? `${name} Coupon` : 'Coupon'),
 selectedProductIds: prev.selectedProductIds.length > 0
 ? prev.selectedProductIds
 : products.map((p) => p.id),
 }));
 };

 const resetDiscountForm = useCallback(() => {
 setEditingPromoId(null);
 setDiscountForm({
 couponName: companyName ? `${companyName} Coupon` : 'Coupon',
 percentageOff: '',
 promoCodeText: '',
 selectedProductIds: customProducts.map((p) => p.id),
 maxRedemptions: '',
 });
 }, [companyName, customProducts]);

 const startEditPromo = (promo) => {
 setEditingPromoId(promo.id);
 setDiscountForm({
 couponName: promo.couponName || (companyName ? `${companyName} Coupon` : 'Coupon'),
 percentageOff: String(promo.percentageOff ?? ''),
 promoCodeText: promo.promoCodeText || '',
 selectedProductIds: promo.productIds?.length ? promo.productIds : customProducts.map((p) => p.id),
 maxRedemptions: promo.maxRedemptions != null ? String(promo.maxRedemptions) : '',
 });
 setPromoDrawerOpen(true);
 };

 const toggleDiscountProduct = (productId) => {
 setDiscountForm((prev) => {
 const selected = prev.selectedProductIds.includes(productId)
 ? prev.selectedProductIds.filter((id) => id !== productId)
 : [...prev.selectedProductIds, productId];
 return { ...prev, selectedProductIds: selected };
 });
 };

 // ── API ───────────────────────────────────────────────────────────────────────
 const loadConfig = async () => {
 setRefreshing(true);
 try {
 const data = await ManagerApi.getConfig();
 applyConfig(data);
 } catch (err) {
 toast.error(err.message || 'Failed to load config');
 } finally {
 setRefreshing(false);
 }
 };

 useEffect(() => {
 const init = async () => {
 try {
 setLoading(true);
 const session = await authClient.getSession();
 if (!session?.user?.id) throw new Error('Not authenticated');

 const profile = await Profiles.getMyProfile(session.user.id);
 if (profile?.role !== 'sys_admin' && profile?.role !== 'company_manager') {
 setIsAuthorized(false);
 return;
 }
 if (profile?.role === 'company_manager' && !profile?.company_id) {
 setIsAuthorized(false);
 setError('No company assigned to your account');
 return;
 }

 setIsAuthorized(true);
 await loadConfig();
 } catch (err) {
 setError(err.message || 'Failed to initialize');
 } finally {
 setLoading(false);
 }
 };
 init();
 }, []);

 // ── Handlers ──────────────────────────────────────────────────────────────────
 const resetPricingForm = useCallback(() => {
 setPricingForm({ planName: '', price: '', interval: 'month', description: '', category: 'other', logoUrl: '' });
 setEditingProductId(null);
 setPricingTouched({ planName: false, price: false });
 }, []);

 const startEditProduct = (product) => {
 setEditingProductId(product.id);
 setPricingForm({
 planName: product.planName || '',
 price: String(product.price ?? ''),
 interval: product.interval === 'year' ? 'year' : 'month',
 description: product.description || '',
 category: product.category || 'other',
 logoUrl: product.logoUrl || '',
 });
 setPricingTouched({ planName: false, price: false });
 setPricingDrawerOpen(true);
 };

 const handleCreateProduct = async (e) => {
 e.preventDefault();
 setSavingPricing(true);
 try {
 const payload = {
 planName: pricingForm.planName.trim(),
 price: Number(pricingForm.price),
 interval: pricingForm.interval,
 description: pricingForm.description.trim(),
 category: pricingForm.category,
 logoUrl: pricingForm.logoUrl?.trim() || '',
 };
 if (editingProductId) {
 await ManagerApi.updateProduct(editingProductId, payload);
 } else {
 await ManagerApi.createProduct(payload);
 }
 closePricingDrawer();
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setSavingPricing(false);
 }
 };

 const handleDeleteProduct = (productId) => {
 triggerConfirm({
 message: t('pricing.deleteConfirm'),
 confirmLabel: t('pricing.delete'),
 cancelLabel: t('pricing.cancelEdit'),
 variant: 'danger',
 onConfirm: async () => {
 setDeletingProductId(productId);
 try {
 await ManagerApi.deleteProduct(productId);
 if (editingProductId === productId) closePricingDrawer();
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setDeletingProductId(null);
 }
 },
 });
 };

 const handleDeactivatePromo = (promoId) => {
 triggerConfirm({
 message: t('promos.deactivatePromoConfirm'),
 confirmLabel: t('promos.deactivate'),
 cancelLabel: t('pricing.cancelEdit'),
 variant: 'warning',
 onConfirm: async () => {
 setDeactivatingPromoId(promoId);
 try {
 await ManagerApi.deactivateDiscount(promoId);
 if (editingPromoId === promoId) closePromoDrawer();
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setDeactivatingPromoId(null);
 }
 },
 });
 };

 const handleDeletePromo = (promoId) => {
 triggerConfirm({
 message: t('promos.deletePromoConfirm'),
 confirmLabel: t('promos.deleteCode'),
 cancelLabel: t('pricing.cancelEdit'),
 variant: 'danger',
 onConfirm: async () => {
 setDeletingPromoId(promoId);
 try {
 await ManagerApi.deleteDiscount(promoId);
 if (editingPromoId === promoId) closePromoDrawer();
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setDeletingPromoId(null);
 }
 },
 });
 };

 const handleDeactivateBypass = (bypassId) => {
 triggerConfirm({
 message: t('promos.deactivateBypassConfirm'),
 confirmLabel: t('promos.deactivate'),
 cancelLabel: t('pricing.cancelEdit'),
 variant: 'warning',
 onConfirm: async () => {
 setDeactivatingBypassId(bypassId);
 try {
 await ManagerApi.deactivateBypassCode(bypassId);
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setDeactivatingBypassId(null);
 }
 },
 });
 };

 const handleDeleteBypass = (bypassId) => {
 triggerConfirm({
 message: t('promos.deleteBypassConfirm'),
 confirmLabel: t('promos.deleteCode'),
 cancelLabel: t('pricing.cancelEdit'),
 variant: 'danger',
 onConfirm: async () => {
 setDeletingBypassId(bypassId);
 try {
 await ManagerApi.deleteBypassCode(bypassId);
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setDeletingBypassId(null);
 }
 },
 });
 };

 const handleCreateDiscount = async (e) => {
 e.preventDefault();
 if (!editingPromoId && discountForm.selectedProductIds.length === 0) {
 toast.error(t('promos.productsRequired'));
 return;
 }
 setSavingDiscount(true);
 try {
 if (editingPromoId) {
 await ManagerApi.updateDiscount(editingPromoId, {
 couponName: discountForm.couponName.trim(),
 maxRedemptions: discountForm.maxRedemptions === '' ? null : Number(discountForm.maxRedemptions),
 });
 } else {
 await ManagerApi.createDiscount({
 couponName: discountForm.couponName.trim(),
 percentageOff: Number(discountForm.percentageOff),
 promoCodeText: discountForm.promoCodeText.trim(),
 productIds: discountForm.selectedProductIds,
 maxRedemptions: discountForm.maxRedemptions === '' ? null : Number(discountForm.maxRedemptions),
 });
 }
 closePromoDrawer();
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setSavingDiscount(false);
 }
 };

 const handleCreateBypass = async (e) => {
 e.preventDefault();
 setSavingBypass(true);
 try {
 await ManagerApi.createBypassCode({
 code: bypassForm.code.trim(),
 expiresAt: bypassForm.expiresAt ? new Date(bypassForm.expiresAt).toISOString() : null,
 });
 setBypassForm({ code: '', expiresAt: '' });
 setBypassDrawerOpen(false);
 await loadConfig();
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setSavingBypass(false);
 }
 };

 // ── Onboarding step helpers ──────────────────────────────────────────────────
 const addQuestion = () => {
 setCustomSteps((prev) => [
 ...prev,
 { id: crypto.randomUUID(), type: 'text', question: '', description: '', options: [] },
 ]);
 };

 const updateStep = (id, patch) => {
 setCustomSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
 };

 const removeStep = (id) => {
 setCustomSteps((prev) => prev.filter((s) => s.id !== id));
 setOptionDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
 };

 const addOptionToStep = (stepId) => {
 const draft = (optionDrafts[stepId] || '').trim();
 if (!draft) return;
 setCustomSteps((prev) =>
 prev.map((s) => {
 if (s.id !== stepId) return s;
 const existing = Array.isArray(s.options) ? s.options : [];
 if (existing.includes(draft)) return s;
 return { ...s, options: [...existing, draft] };
 })
 );
 setOptionDrafts((prev) => ({ ...prev, [stepId]: '' }));
 };

 const removeOptionFromStep = (stepId, optionIndex) => {
 setCustomSteps((prev) =>
 prev.map((s) => {
 if (s.id !== stepId) return s;
 const options = (Array.isArray(s.options) ? s.options : []).filter((_, i) => i !== optionIndex);
 return { ...s, options };
 })
 );
 };

 const handleDragEnd = (event) => {
 const { active, over } = event;
 if (active.id !== over?.id) {
 setCustomSteps((steps) => {
 const oldIndex = steps.findIndex((s) => s.id === active.id);
 const newIndex = steps.findIndex((s) => s.id === over.id);
 return arrayMove(steps, oldIndex, newIndex);
 });
 }
 };

 const handleSaveOnboarding = async () => {
 setSavingOnboarding(true);
 try {
 const result = await ManagerApi.saveOnboardingSteps(customSteps);
 setCustomSteps(result.customSteps || []);
 toast.success(t('success.saved'));
 } catch (err) {
 toast.error(err.message);
 } finally {
 setSavingOnboarding(false);
 }
 };

 const tabIcon = (tab) => {
 if (tab === 'pricing') return DollarSign;
 if (tab === 'promos') return Tag;
 return ClipboardList;
 };

 // ── Loading screen ───────────────────────────────────────────────────────────
 if (loading) {
 return (
 <div className={`min-h-screen flex items-center justify-center ${themeClasses.bgPrimary}`} dir={direction}>
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 className="flex flex-col items-center gap-4"
 >
 <div className="w-14 h-14 rounded-lg bg-forest flex items-center justify-center ">
 <RefreshCw className="w-7 h-7 text-white animate-spin" />
 </div>
 <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('loading')}</p>
 </motion.div>
 </div>
 );
 }

 // ── Unauthorized screen ──────────────────────────────────────────────────────
 if (!isAuthorized) {
 return (
 <div className={`min-h-screen p-8 flex items-center justify-center ${themeClasses.bgPrimary}`} dir={direction}>
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 className={`${cardBase} max-w-md w-full text-center`}
 >
 <div className="w-16 h-16 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center mx-auto mb-5">
 <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
 </div>
 <h1 className={`text-lg font-bold ${themeClasses.textPrimary} mb-2`}>{t('access.restricted')}</h1>
 <p className={`text-sm ${themeClasses.textMuted}`}>{t('access.message')}</p>
 {error && (
 <p className="mt-3 text-xs text-red-500 dark:text-red-400">{error}</p>
 )}
 </motion.div>
 </div>
 );
 }

 // ── Field validation helpers ─────────────────────────────────────────────────
 const fieldInputClass = (touched, value) => {
 if (!touched) return themeClasses.inputField;
 if (value) return `${themeClasses.inputField} !border-forest/60 pr-9`;
 return `${themeClasses.inputField} !border-red-500/50 !ring-red-500/15 pr-9`;
 };

 // ── Render ────────────────────────────────────────────────────────────────────
 return (
 <div className={`min-h-screen ${themeClasses.bgPrimary} pb-20`} dir={direction}>

 {/* ── Confirm Modal ── */}
 <AnimatePresence>
 {confirmState.open && (
 <ConfirmModal
 key="confirm"
 title={confirmState.title}
 message={confirmState.message}
 confirmLabel={confirmState.confirmLabel}
 cancelLabel={confirmState.cancelLabel}
 onConfirm={executeConfirm}
 onCancel={closeConfirm}
 variant={confirmState.variant}
 />
 )}
 </AnimatePresence>

 {/* ── Pricing Drawer ── */}
 <Drawer
 open={pricingDrawerOpen}
 onClose={closePricingDrawer}
 title={editingProductId ? t('pricing.editing') : t('pricing.create')}
 isDarkMode={isDarkMode}
 isRTL={isRTL}
 dataTour="playbook-company-product-drawer"
 >
 <form onSubmit={handleCreateProduct} className="space-y-5">
 {editingProductId && (
 <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-md text-xs ${isDarkMode ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
 <Pencil className="w-3.5 h-3.5 mt-0.5 shrink-0" />
 {t('pricing.editing')}
 </div>
 )}
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('pricing.planName')}
 </label>
 <div className="relative">
 <input
 className={fieldInputClass(pricingTouched.planName, pricingForm.planName)}
 value={pricingForm.planName}
 onChange={(e) => setPricingForm((f) => ({ ...f, planName: e.target.value }))}
 onBlur={() => setPricingTouched((p) => ({ ...p, planName: true }))}
 required
 />
 {pricingTouched.planName && pricingForm.planName && (
 <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest pointer-events-none" />
 )}
 {pricingTouched.planName && !pricingForm.planName && (
 <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none" />
 )}
 </div>
 </div>
 <AssetImageField
 label={t('pricing.productLogo')}
 hint={t('pricing.productLogoHint')}
 folder="products"
 value={pricingForm.logoUrl || ''}
 onChange={(logoUrl) => setPricingForm((f) => ({ ...f, logoUrl }))}
 shell={{
 label: themeClasses.textMuted,
 muted: themeClasses.textMuted,
 contentBox: isDarkMode ? 'border border-line bg-canvas/40 rounded-md' : 'border border-line bg-canvas rounded-md',
 input: isDarkMode,
 }}
 />
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('pricing.price')}
 </label>
 <div className="relative">
 <input
 type="number"
 min="0.01"
 step="0.01"
 className={fieldInputClass(pricingTouched.price, pricingForm.price)}
 value={pricingForm.price}
 onChange={(e) => setPricingForm((f) => ({ ...f, price: e.target.value }))}
 onBlur={() => setPricingTouched((p) => ({ ...p, price: true }))}
 required
 />
 {pricingTouched.price && pricingForm.price && (
 <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest pointer-events-none" />
 )}
 {pricingTouched.price && !pricingForm.price && (
 <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none" />
 )}
 </div>
 </div>
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('pricing.interval')}
 </label>
 <select
 className={themeClasses.select}
 value={pricingForm.interval}
 onChange={(e) => setPricingForm((f) => ({ ...f, interval: e.target.value }))}
 >
 <option value="month">{t('pricing.month')}</option>
 <option value="year">{t('pricing.year')}</option>
 </select>
 </div>
 </div>
 <fieldset>
 <legend className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('pricing.category')}
 </legend>
 <div className={`flex gap-1 p-1 rounded-md ${isDarkMode ? 'bg-canvas/60' : 'bg-surface'}`} role="radiogroup" aria-label={t('pricing.category')}>
 {[
 { id: 'nutrition', label: t('pricing.categoryNutrition'), Icon: Utensils },
 { id: 'training', label: t('pricing.categoryTraining'), Icon: Dumbbell },
 { id: 'other', label: t('pricing.categoryOther'), Icon: LayoutGrid },
 ].map(({ id, label, Icon }) => {
 const selected = (pricingForm.category || 'other') === id;
 return (
 <label
 key={id}
 className={`flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-xs font-semibold rounded-lg cursor-pointer transition-all select-none ${
 selected
 ? 'bg-forest text-white '
 : isDarkMode ? 'text-ink-muted hover:text-ink-dark' : 'text-ink-muted hover:text-ink'
 }`}
 >
 <input
 type="radio"
 name="pricing-category"
 value={id}
 checked={selected}
 onChange={() => setPricingForm((f) => ({ ...f, category: id }))}
 className="sr-only"
 />
 <Icon className="w-4 h-4 pointer-events-none" />
 <span className="pointer-events-none">{label}</span>
 </label>
 );
 })}
 </div>
 </fieldset>
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('pricing.description')}
 </label>
 <textarea
 className={`${themeClasses.inputField} min-h-[80px] resize-y`}
 value={pricingForm.description}
 maxLength={PRODUCT_DESCRIPTION_MAX}
 onChange={(e) => setPricingForm((f) => ({ ...f, description: e.target.value }))}
 placeholder={t('pricing.descriptionHint')}
 />
 <p className={`text-xs mt-1 text-end ${themeClasses.textMuted}`}>
 {pricingForm.description.length}/{PRODUCT_DESCRIPTION_MAX}
 </p>
 </div>
 <div className="flex gap-3 pt-1">
 <button type="submit" disabled={savingPricing} className={`${themeClasses.btnPrimary} flex-1`}>
 {savingPricing && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
 {editingProductId ? t('pricing.update') : t('pricing.create')}
 </button>
 <button type="button" onClick={closePricingDrawer} className={themeClasses.btnSecondary}>
 {t('pricing.cancelEdit')}
 </button>
 </div>
 </form>
 </Drawer>

 {/* ── Promo Drawer ── */}
 <Drawer
 open={promoDrawerOpen}
 onClose={closePromoDrawer}
 title={editingPromoId ? t('promos.editing') : t('promos.discountTitle')}
 isDarkMode={isDarkMode}
 isRTL={isRTL}
 >
 <form onSubmit={handleCreateDiscount} className="space-y-5">
 {editingPromoId && (
 <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-md text-xs ${isDarkMode ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
 <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
 {t('promos.editHint')}
 </div>
 )}
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.couponName')}
 </label>
 <input
 className={themeClasses.inputField}
 value={discountForm.couponName}
 onChange={(e) => setDiscountForm((f) => ({ ...f, couponName: e.target.value }))}
 placeholder={companyName ? `${companyName} Coupon` : 'Coupon'}
 required
 />
 <p className={`text-xs mt-1 ${themeClasses.textMuted}`}>{t('promos.couponNameHint')}</p>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.percentOff')}
 </label>
 <input
 type="number"
 min="1"
 max="100"
 className={`${themeClasses.inputField} ${editingPromoId ? 'opacity-50 cursor-not-allowed' : ''}`}
 value={discountForm.percentageOff}
 onChange={(e) => setDiscountForm((f) => ({ ...f, percentageOff: e.target.value }))}
 disabled={!!editingPromoId}
 required={!editingPromoId}
 />
 {editingPromoId && <p className={`text-xs mt-1 ${themeClasses.textMuted}`}>{t('promos.readOnly')}</p>}
 </div>
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.code')}
 </label>
 <input
 className={`${themeClasses.inputField} ${editingPromoId ? 'opacity-50 cursor-not-allowed' : ''}`}
 value={discountForm.promoCodeText}
 onChange={(e) => setDiscountForm((f) => ({ ...f, promoCodeText: e.target.value.toUpperCase() }))}
 disabled={!!editingPromoId}
 required={!editingPromoId}
 />
 {editingPromoId && <p className={`text-xs mt-1 ${themeClasses.textMuted}`}>{t('promos.readOnly')}</p>}
 </div>
 </div>
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.maxRedemptions')}
 </label>
 <input
 type="number"
 min="1"
 step="1"
 className={themeClasses.inputField}
 value={discountForm.maxRedemptions}
 onChange={(e) => setDiscountForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
 placeholder={t('promos.unlimited')}
 />
 <p className={`text-xs mt-1 ${themeClasses.textMuted}`}>{t('promos.maxRedemptionsHint')}</p>
 </div>
 <div>
 <div className="flex items-center justify-between mb-2">
 <label className={`text-xs font-semibold uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.products')}
 </label>
 {!editingPromoId && (
 <div className="flex gap-3">
 <button
 type="button"
 className="text-xs font-semibold text-forest dark:text-forest-ondark hover:underline"
 onClick={() => setDiscountForm((f) => ({ ...f, selectedProductIds: customProducts.map((p) => p.id) }))}
 >
 {t('promos.selectAll')}
 </button>
 <button
 type="button"
 className={`text-xs font-semibold ${themeClasses.textMuted} hover:underline`}
 onClick={() => setDiscountForm((f) => ({ ...f, selectedProductIds: [] }))}
 >
 {t('promos.selectNone')}
 </button>
 </div>
 )}
 </div>
 {customProducts.length === 0 ? (
 <p className={`text-sm ${themeClasses.textMuted}`}>{t('pricing.empty')}</p>
 ) : (
 <div className={`rounded-md border p-2 space-y-1 max-h-44 overflow-y-auto ${
 isDarkMode ? 'border-line bg-canvas/40' : 'border-line bg-canvas'
 } ${editingPromoId ? 'opacity-60' : ''}`}>
 {customProducts.map((product) => {
 const checked = discountForm.selectedProductIds.includes(product.id);
 return (
 <label
 key={product.id}
 className={`flex items-center gap-3 px-3 py-2 rounded-lg ${editingPromoId ? 'cursor-default' : 'cursor-pointer'} transition-colors ${
 checked ? (isDarkMode ? 'bg-forest-faint0/10' : 'bg-forest-faint') : 'hover:bg-surface dark:hover:bg-canvas-dark/50'
 }`}
 >
 <input
 type="checkbox"
 checked={checked}
 onChange={() => toggleDiscountProduct(product.id)}
 disabled={!!editingPromoId}
 className="rounded border-line text-forest focus:ring-forest"
 />
 <span className={`text-sm flex-1 ${themeClasses.textPrimary}`}>
 {product.planName}
 <span className={`block text-xs ${themeClasses.textMuted}`}>
 ${product.price} / {product.interval === 'year' ? t('pricing.year') : t('pricing.month')}
 </span>
 </span>
 </label>
 );
 })}
 </div>
 )}
 </div>
 <div className="flex gap-3 pt-1">
 <button
 type="submit"
 disabled={savingDiscount || (!editingPromoId && (customProducts.length === 0 || discountForm.selectedProductIds.length === 0))}
 className={`${themeClasses.btnPrimary} flex-1`}
 >
 {savingDiscount && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
 {editingPromoId ? t('promos.update') : t('promos.createDiscount')}
 </button>
 <button type="button" onClick={closePromoDrawer} className={themeClasses.btnSecondary}>
 {t('promos.cancelEdit')}
 </button>
 </div>
 </form>
 </Drawer>

 {/* ── Bypass Drawer ── */}
 <Drawer
 open={bypassDrawerOpen}
 onClose={() => setBypassDrawerOpen(false)}
 title={t('promos.bypassTitle')}
 isDarkMode={isDarkMode}
 isRTL={isRTL}
 >
 <div className="space-y-5">
 <div className={`rounded-md border p-3.5 space-y-1.5 ${isDarkMode ? 'bg-violet-500/10 border-violet-500/25 text-violet-300' : 'bg-violet-50 border-violet-200 text-violet-800'}`}>
 <div className="flex items-center gap-2 text-xs font-semibold">
 <Zap className="w-3.5 h-3.5" />
 {t('promos.bypassTitle')}
 </div>
 <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>{t('promos.bypassStripeNote')}</p>
 <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>{t('promos.bypassWhere')}</p>
 </div>
 <form onSubmit={handleCreateBypass} className="space-y-4">
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.bypassCode')}
 </label>
 <input
 className={themeClasses.inputField}
 value={bypassForm.code}
 onChange={(e) => setBypassForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
 required
 />
 </div>
 <div>
 <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${themeClasses.textMuted}`}>
 {t('promos.expiresAt')}
 </label>
 <input
 type="datetime-local"
 className={themeClasses.inputField}
 value={bypassForm.expiresAt}
 onChange={(e) => setBypassForm((f) => ({ ...f, expiresAt: e.target.value }))}
 />
 </div>
 <div className="flex gap-3 pt-1">
 <button type="submit" disabled={savingBypass} className={`${themeClasses.btnPrimary} flex-1`}>
 {savingBypass && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
 {t('promos.createBypass')}
 </button>
 <button type="button" onClick={() => setBypassDrawerOpen(false)} className={themeClasses.btnSecondary}>
 {t('pricing.cancelEdit')}
 </button>
 </div>
 </form>
 </div>
 </Drawer>

 {/* ── Page content ── */}
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
 {/* Header — top-level page (reached from the top bar), so no back button */}
 <div className="flex items-start sm:items-end justify-between gap-3 mb-5 sm:mb-8">
 <div className="flex items-start gap-3">
 <div>
 <h1 className={`font-display text-2xl sm:text-4xl font-medium leading-tight ${themeClasses.textPrimary}`}>{t('page.title')}</h1>
 <p className={`text-sm mt-0.5 ${themeClasses.textMuted}`}>
 {companyName && (
 <span className="font-semibold text-forest dark:text-forest-ondark">{companyName} · </span>
 )}
 {t('page.subtitle')}
 </p>
 </div>
 </div>
 <motion.button
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 type="button"
 onClick={loadConfig}
 disabled={refreshing}
 aria-label={t('refresh')}
 className={`${themeClasses.btnSecondary} shrink-0 !px-3 sm:!px-4`}
 >
 <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
 <span className="hidden sm:inline">{t('refresh')}</span>
 </motion.button>
 </div>

 {error && (
 <div className="mb-4 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
 <AlertCircle className="w-4 h-4 shrink-0" />
 {error}
 </div>
 )}

 {/* Tab bar with animated pill */}
 <div data-tour="playbook-company-pricing-tabs" className={`mb-5 sm:mb-6 flex p-1 rounded-lg w-full sm:w-fit ${isDarkMode ? 'bg-surface-dark' : 'bg-surface/80'}`}>
 {TABS.map((tab) => {
 const Icon = tabIcon(tab);
 const isActive = activeTab === tab;
 return (
 <button
 key={tab}
 type="button"
 onClick={() => setActiveTab(tab)}
 className={`relative flex flex-1 sm:flex-none items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-5 py-2.5 rounded-md text-[13px] sm:text-sm font-semibold transition-colors select-none ${
 isActive ? 'text-white' : isDarkMode ? 'text-ink-muted hover:text-ink' : 'text-ink-muted hover:text-ink'
 }`}
 >
 {isActive && (
 <motion.div
 layoutId="activeTabPill"
 className="absolute inset-0 rounded-md bg-forest "
 transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
 />
 )}
 <span className="relative z-10 flex min-w-0 items-center gap-1.5 sm:gap-2">
 <Icon className="w-4 h-4 shrink-0" />
 <span className="truncate">{t(`tab.${tab}`)}</span>
 </span>
 </button>
 );
 })}
 </div>

 {/* Tab content */}
 <AnimatePresence mode="wait">
 <motion.div
 key={activeTab}
 initial={{ opacity: 0, y: 8 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -8 }}
 transition={{ duration: 0.16 }}
 >
 {/* ══ PRICING TAB ══════════════════════════════════════════════════ */}
 {activeTab === 'pricing' && (
 <div className="space-y-4">
 <div className="flex items-center justify-between px-1">
 <div>
 <h2 className={`text-base font-semibold ${themeClasses.textPrimary}`}>{t('pricing.title')}</h2>
 <p className={`text-xs mt-0.5 ${themeClasses.textMuted}`}>
 {customProducts.length > 0
 ? `${customProducts.length} plan${customProducts.length !== 1 ? 's' : ''}`
 : ''}
 </p>
 </div>
 <motion.button
 data-tour="playbook-company-new-product"
 whileHover={{ scale: 1.03 }}
 whileTap={{ scale: 0.97 }}
 type="button"
 onClick={() => { resetPricingForm(); setPricingDrawerOpen(true); }}
 className={themeClasses.btnPrimary}
 >
 <Plus className="w-4 h-4" />
 {t('pricing.create')}
 </motion.button>
 </div>

 {customProducts.length === 0 ? (
 <div className={cardBase}>
 <EmptyState
 icon={PackageOpen}
 title={t('pricing.empty')}
 subtitle={t('pricing.emptySubtitle')}
 ctaLabel={t('pricing.create')}
 onCta={() => { resetPricingForm(); setPricingDrawerOpen(true); }}
 isDarkMode={isDarkMode}
 />
 </div>
 ) : (
 <>
 <ul className="md:hidden divide-y divide-line dark:divide-line-dark rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
 {customProducts.map((p) => (
 <li key={p.id} className={`flex items-start gap-3 px-4 py-3 ${editingProductId === p.id ? 'bg-forest-faint dark:bg-forest-faint-dark' : ''}`}>
 <div className="shrink-0 w-10 h-10 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark overflow-hidden flex items-center justify-center">
 {p.logoUrl ? <img src={p.logoUrl} alt="" className="w-full h-full object-contain p-1" /> : <PackageOpen className="w-4 h-4 text-ink-muted" aria-hidden />}
 </div>
 <div className="min-w-0 flex-1">
 <div className="flex items-baseline justify-between gap-2">
 <p className="truncate text-sm font-semibold text-ink dark:text-ink-dark">{p.planName}</p>
 <p className="shrink-0 text-sm font-semibold tabular-nums text-ink dark:text-ink-dark">
 ${p.price}<span className="text-[11px] font-normal text-ink-muted">/{p.interval === 'year' ? t('pricing.year') : t('pricing.month')}</span>
 </p>
 </div>
 <p className="mt-0.5 text-[12px] text-ink-muted">
 {p.category === 'nutrition' ? t('pricing.categoryNutrition') : p.category === 'training' ? t('pricing.categoryTraining') : t('pricing.categoryOther')}
 </p>
 {p.description && <p className="mt-1 line-clamp-2 text-[12px] text-ink-muted">{p.description}</p>}
 <code className="mt-1 block truncate text-[11px] font-mono text-ink-muted">{p.stripePriceId}</code>
 </div>
 <div className="flex shrink-0 flex-col">
 <button aria-label={t('pricing.edit')} type="button" onClick={() => startEditProduct(p)} disabled={deletingProductId === p.id} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-forest hover:bg-forest-faint dark:hover:bg-forest-faint-dark focus-visible:ring-forest`}>
 <Pencil className="w-4 h-4" />
 </button>
 <button aria-label={t('pricing.delete')} type="button" onClick={() => handleDeleteProduct(p.id)} disabled={deletingProductId === p.id} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 focus-visible:ring-red-500`}>
 {deletingProductId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </li>
 ))}
 </ul>
 <div className="relative hidden md:block rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
 <div
 className="overflow-x-auto custom-scrollbar"
 aria-label={t('pricing.scrollMore')}
 >
 <table className="w-full text-start border-collapse min-w-[920px]" dir={direction}>
 <thead>
 <tr className="border-b border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark">
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap">{t('pricing.planName')}</th>
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap">{t('pricing.category')}</th>
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap">{t('pricing.interval')}</th>
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap text-end">{t('pricing.price')}</th>
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap">{t('pricing.description')}</th>
 <th className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted whitespace-nowrap">{t('pricing.stripeId')}</th>
 <th className="px-2 py-1.5 w-[1%] whitespace-nowrap" />
 </tr>
 </thead>
 <tbody className="divide-y divide-line dark:divide-line-dark">
 {customProducts.map((p) => (
 <tr
 key={p.id}
 className={`hover:bg-canvas dark:hover:bg-canvas-dark/40 transition-colors duration-120 ${
 editingProductId === p.id ? 'bg-forest-faint dark:bg-forest-faint-dark' : ''
 }`}
 >
 <td className="px-3 py-1.5">
 <div className="flex items-center gap-2 min-w-0">
 <div className="shrink-0 w-7 h-7 rounded border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark overflow-hidden flex items-center justify-center">
 {p.logoUrl ? (
 <img src={p.logoUrl} alt="" className="w-full h-full object-contain p-0.5" />
 ) : (
 <PackageOpen className="w-3.5 h-3.5 text-ink-muted" aria-hidden />
 )}
 </div>
 <span className="font-medium text-[13px] text-ink dark:text-ink-dark truncate max-w-[180px]">{p.planName}</span>
 </div>
 </td>
 <td className="px-3 py-1.5">
 <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted border border-line dark:border-line-dark whitespace-nowrap">
 {p.category === 'nutrition' && <Utensils className="w-3 h-3" />}
 {p.category === 'training' && <Dumbbell className="w-3 h-3" />}
 {(!p.category || p.category === 'other') && <LayoutGrid className="w-3 h-3" />}
 {p.category === 'nutrition' ? t('pricing.categoryNutrition')
 : p.category === 'training' ? t('pricing.categoryTraining')
 : t('pricing.categoryOther')}
 </span>
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink dark:text-ink-dark whitespace-nowrap">
 {p.interval === 'year' ? t('pricing.year') : t('pricing.month')}
 </td>
 <td className="px-3 py-1.5 text-[13px] font-medium tabular-nums text-ink dark:text-ink-dark whitespace-nowrap text-end">
 ${p.price}
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink-muted max-w-[200px]">
 <span className="block truncate">{p.description || '—'}</span>
 </td>
 <td className="px-3 py-1.5">
 <code className="text-[11px] px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted font-mono whitespace-nowrap">
 {p.stripePriceId}
 </code>
 </td>
 <td className="px-2 py-1">
 <div className="flex items-center justify-end gap-0.5">
 <button aria-label={t('pricing.edit')} type="button" onClick={() => startEditProduct(p)} disabled={deletingProductId === p.id} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-forest hover:bg-forest-faint dark:hover:bg-forest-faint-dark rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest" title={t('pricing.edit')}>
 <Pencil className="w-4 h-4" />
 </button>
 <button aria-label={t('pricing.delete')} type="button" onClick={() => handleDeleteProduct(p.id)} disabled={deletingProductId === p.id} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500" title={t('pricing.delete')}>
 {deletingProductId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 <div
 className={`pointer-events-none absolute inset-y-0 w-10 ${isRTL ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l'} from-surface dark:from-surface-dark to-transparent`}
 aria-hidden
 />
 <div
 className={`pointer-events-none absolute inset-y-0 flex items-center ${isRTL ? 'left-1' : 'right-1'} text-ink-muted`}
 aria-hidden
 title={t('pricing.scrollMore')}
 >
 {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
 </div>
 </div>
 </>
 )}
 </div>
 )}

 {/* ══ PROMOS TAB ═══════════════════════════════════════════════════ */}
 {activeTab === 'promos' && (
 <div className="space-y-4">
 {/* Action row */}
 <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:px-1">
 <motion.button
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 type="button"
 onClick={() => { resetDiscountForm(); setPromoDrawerOpen(true); }}
 className={`${themeClasses.btnPrimary} justify-center`}
 >
 <Tag className="w-4 h-4" />
 {t('promos.createDiscount')}
 </motion.button>
 <motion.button
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 type="button"
 onClick={() => setBypassDrawerOpen(true)}
 className={`${themeClasses.btnSecondary} justify-center`}
 >
 <Zap className="w-4 h-4" />
 {t('promos.bypassTitle')}
 </motion.button>
 </div>

 {/* Active codes list */}
 <div className="relative rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
 <div className="px-3 py-2 border-b border-line dark:border-line-dark">
 <h2 className="text-[15px] font-semibold text-ink dark:text-ink-dark">{t('promos.listTitle')}</h2>
 </div>
 {promoCodes.length === 0 && bypassCodes.length === 0 ? (
 <EmptyState
 icon={Ticket}
 title={t('promos.empty')}
 subtitle={t('promos.emptySubtitle')}
 isDarkMode={isDarkMode}
 />
 ) : (
 <>
 <ul className="md:hidden divide-y divide-line dark:divide-line-dark">
 {promoCodes.map((p) => {
 const promoActive = isCodeActive(p);
 const promoBusy = deactivatingPromoId === p.id || deletingPromoId === p.id;
 return (
 <li key={p.id} className={`flex items-start gap-3 px-4 py-3 ${editingPromoId === p.id ? 'bg-forest-faint dark:bg-forest-faint-dark' : ''} ${!promoActive ? 'opacity-55' : ''}`}>
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-1.5">
 <span className="font-mono text-sm font-semibold text-ink dark:text-ink-dark">{p.promoCodeText}</span>
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted border border-line dark:border-line-dark">{t('promos.stripeTag')}</span>
 {!promoActive && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">{t('promos.inactiveTag')}</span>}
 </div>
 <p className="mt-1 text-[13px] text-ink dark:text-ink-dark">
 {p.percentageOff}% · {p.maxRedemptions != null ? `${p.maxRedemptions} ${t('promos.uses')}` : t('promos.unlimited')}
 </p>
 {(p.couponName || p.productNames?.length > 0) && (
 <p className="mt-0.5 truncate text-[12px] text-ink-muted">{[p.couponName, p.productNames?.join(', ')].filter(Boolean).join(' · ')}</p>
 )}
 </div>
 <div className="flex shrink-0 items-center">
 {promoActive && (
 <button aria-label={t('promos.edit')} type="button" onClick={() => startEditPromo(p)} disabled={promoBusy} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-forest hover:bg-forest-faint dark:hover:bg-forest-faint-dark focus-visible:ring-forest`}>
 <Pencil className="w-4 h-4" />
 </button>
 )}
 {promoActive && (
 <button aria-label={t('promos.deactivate')} type="button" onClick={() => handleDeactivatePromo(p.id)} disabled={promoBusy} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 focus-visible:ring-amber-500`}>
 {deactivatingPromoId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
 </button>
 )}
 <button aria-label={t('promos.deleteCode')} type="button" onClick={() => handleDeletePromo(p.id)} disabled={promoBusy} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 focus-visible:ring-red-500`}>
 {deletingPromoId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </li>
 );
 })}
 {bypassCodes.map((b) => {
 const bypassActive = isCodeActive(b);
 const bypassBusy = deactivatingBypassId === b.id || deletingBypassId === b.id;
 return (
 <li key={b.id} className={`flex items-start gap-3 px-4 py-3 ${!bypassActive ? 'opacity-55' : ''}`}>
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-1.5">
 <span className="font-mono text-sm font-semibold text-ink dark:text-ink-dark">{b.code}</span>
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted border border-line dark:border-line-dark">{t('promos.appOnlyTag')}</span>
 {!bypassActive && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">{t('promos.inactiveTag')}</span>}
 </div>
 <p className="mt-1 text-[13px] text-ink dark:text-ink-dark">100% bypass{b.expiresAt ? ` · expires ${new Date(b.expiresAt).toLocaleDateString()}` : ''}</p>
 </div>
 <div className="flex shrink-0 items-center">
 {bypassActive && (
 <button aria-label={t('promos.deactivate')} type="button" onClick={() => handleDeactivateBypass(b.id)} disabled={bypassBusy} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 focus-visible:ring-amber-500`}>
 {deactivatingBypassId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
 </button>
 )}
 <button aria-label={t('promos.deleteCode')} type="button" onClick={() => handleDeleteBypass(b.id)} disabled={bypassBusy} className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted transition-colors outline-none focus-visible:ring-2 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 focus-visible:ring-red-500`}>
 {deletingBypassId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </li>
 );
 })}
 </ul>
 <div className="hidden md:block overflow-x-auto custom-scrollbar" aria-label={t('pricing.scrollMore')}>
 <table className="w-full text-start border-collapse min-w-[780px]" dir={direction}>
 <tbody className="divide-y divide-line dark:divide-line-dark">
 {promoCodes.map((p) => {
 const promoActive = isCodeActive(p);
 const promoBusy = deactivatingPromoId === p.id || deletingPromoId === p.id;
 return (
 <tr
 key={p.id}
 className={`hover:bg-canvas dark:hover:bg-canvas-dark/40 transition-colors duration-120 ${
 editingPromoId === p.id ? 'bg-forest-faint dark:bg-forest-faint-dark' : ''
 } ${!promoActive ? 'opacity-55' : ''}`}
 >
 <td className="px-3 py-1.5 whitespace-nowrap">
 <span className="font-mono font-medium text-[13px] text-ink dark:text-ink-dark">{p.promoCodeText}</span>
 </td>
 <td className="px-3 py-1.5 whitespace-nowrap">
 <div className="flex items-center gap-1">
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted border border-line dark:border-line-dark">
 {t('promos.stripeTag')}
 </span>
 {!promoActive && (
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">
 {t('promos.inactiveTag')}
 </span>
 )}
 </div>
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink-muted whitespace-nowrap max-w-[160px]">
 <span className="block truncate">{p.couponName || '—'}</span>
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink-muted whitespace-nowrap max-w-[180px]">
 <span className="block truncate">{p.productNames?.length > 0 ? p.productNames.join(', ') : '—'}</span>
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink-muted whitespace-nowrap">
 {p.percentageOff}% · {p.maxRedemptions != null ? `${p.maxRedemptions} ${t('promos.uses')}` : t('promos.unlimited')}
 </td>
 <td className="px-2 py-1">
 <div className="flex items-center justify-end gap-0.5">
 {promoActive && (
 <button aria-label={t('promos.edit')} type="button" onClick={() => startEditPromo(p)} disabled={promoBusy} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-forest hover:bg-forest-faint dark:hover:bg-forest-faint-dark rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest" title={t('promos.edit')}>
 <Pencil className="w-4 h-4" />
 </button>
 )}
 {promoActive && (
 <button aria-label={t('promos.deactivate')} type="button" onClick={() => handleDeactivatePromo(p.id)} disabled={promoBusy} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-500" title={t('promos.deactivate')}>
 {deactivatingPromoId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
 </button>
 )}
 <button aria-label={t('promos.deleteCode')} type="button" onClick={() => handleDeletePromo(p.id)} disabled={promoBusy} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500" title={t('promos.deleteCode')}>
 {deletingPromoId === p.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </td>
 </tr>
 );
 })}
 {bypassCodes.map((b) => {
 const bypassActive = isCodeActive(b);
 const bypassBusy = deactivatingBypassId === b.id || deletingBypassId === b.id;
 return (
 <tr
 key={b.id}
 className={`hover:bg-canvas dark:hover:bg-canvas-dark/40 transition-colors duration-120 ${!bypassActive ? 'opacity-55' : ''}`}
 >
 <td className="px-3 py-1.5 whitespace-nowrap">
 <span className="font-mono font-medium text-[13px] text-ink dark:text-ink-dark">{b.code}</span>
 </td>
 <td className="px-3 py-1.5 whitespace-nowrap">
 <div className="flex items-center gap-1">
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-canvas dark:bg-canvas-dark text-ink-muted border border-line dark:border-line-dark">
 {t('promos.appOnlyTag')}
 </span>
 {!bypassActive && (
 <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">
 {t('promos.inactiveTag')}
 </span>
 )}
 </div>
 </td>
 <td className="px-3 py-1.5 text-[13px] text-ink-muted whitespace-nowrap" colSpan={2}>
 100% bypass{b.expiresAt ? ` · expires ${new Date(b.expiresAt).toLocaleDateString()}` : ''}
 </td>
 <td className="px-3 py-1.5" />
 <td className="px-2 py-1">
 <div className="flex items-center justify-end gap-0.5">
 {bypassActive && (
 <button aria-label={t('promos.deactivate')} type="button" onClick={() => handleDeactivateBypass(b.id)} disabled={bypassBusy} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-500" title={t('promos.deactivate')}>
 {deactivatingBypassId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
 </button>
 )}
 <button aria-label={t('promos.deleteCode')} type="button" onClick={() => handleDeleteBypass(b.id)} disabled={bypassBusy} className="p-1.5 min-h-9 min-w-9 flex items-center justify-center text-ink-muted hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500" title={t('promos.deleteCode')}>
 {deletingBypassId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 </button>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 <div
 className={`pointer-events-none absolute top-10 bottom-0 w-10 hidden md:block ${isRTL ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l'} from-surface dark:from-surface-dark to-transparent`}
 aria-hidden
 />
 <div
 className={`pointer-events-none absolute top-10 bottom-0 hidden md:flex items-center ${isRTL ? 'left-1' : 'right-1'} text-ink-muted`}
 aria-hidden
 title={t('pricing.scrollMore')}
 >
 {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
 </div>
 </>
 )}
 </div>
 </div>
 )}

 {/* ══ ONBOARDING TAB ═══════════════════════════════════════════════ */}
 {activeTab === 'onboarding' && (
 <div className="space-y-4">
 <div className="flex flex-wrap items-center justify-between gap-3 px-1">
 <div>
 <h2 className={`text-base font-semibold ${themeClasses.textPrimary}`}>{t('onboarding.title')}</h2>
 <p className={`text-xs mt-0.5 ${themeClasses.textMuted}`}>
 {customSteps.length > 0 ? `${customSteps.length} question${customSteps.length !== 1 ? 's' : ''} · drag to reorder` : ''}
 </p>
 </div>
 <div className="flex items-center gap-2">
 {customSteps.length > 0 && (
 <motion.button
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 type="button"
 onClick={handleSaveOnboarding}
 disabled={savingOnboarding}
 className={`${themeClasses.btnPrimary} hidden sm:inline-flex`}
 >
 {savingOnboarding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
 {t('onboarding.save')}
 </motion.button>
 )}
 <motion.button
 whileHover={{ scale: 1.03 }}
 whileTap={{ scale: 0.97 }}
 type="button"
 onClick={addQuestion}
 className={themeClasses.btnSecondary}
 >
 <Plus className="w-4 h-4" />
 {t('onboarding.add')}
 </motion.button>
 </div>
 </div>

 {customSteps.length === 0 ? (
 <div className={cardBase}>
 <EmptyState
 icon={HelpCircle}
 title={t('onboarding.empty')}
 subtitle={t('onboarding.emptySubtitle')}
 ctaLabel={t('onboarding.add')}
 onCta={addQuestion}
 isDarkMode={isDarkMode}
 />
 </div>
 ) : (
 <DndContext
 sensors={sensors}
 collisionDetection={closestCenter}
 onDragEnd={handleDragEnd}
 >
 <SortableContext
 items={customSteps.map((s) => s.id)}
 strategy={verticalListSortingStrategy}
 >
 <div className="space-y-3">
 <AnimatePresence initial={false}>
 {customSteps.map((step, idx) => (
 <SortableQuestion
 key={step.id}
 step={step}
 idx={idx}
 onUpdate={updateStep}
 onRemove={removeStep}
 onAddOption={addOptionToStep}
 onRemoveOption={removeOptionFromStep}
 optionDraft={optionDrafts[step.id] || ''}
 onOptionDraftChange={(stepId, val) => setOptionDrafts((prev) => ({ ...prev, [stepId]: val }))}
 t={t}
 themeClasses={themeClasses}
 isDarkMode={isDarkMode}
 />
 ))}
 </AnimatePresence>
 </div>
 </SortableContext>
 </DndContext>
 )}

 {customSteps.length > 0 && (
 <div className="sticky bottom-0 z-10 -mx-4 border-t border-line bg-canvas/95 px-4 py-3 backdrop-blur dark:border-line-dark dark:bg-canvas-dark/95 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:backdrop-blur-none sm:dark:bg-transparent">
 <motion.button
 whileHover={{ scale: 1.01 }}
 whileTap={{ scale: 0.99 }}
 type="button"
 onClick={handleSaveOnboarding}
 disabled={savingOnboarding}
 className={`${themeClasses.btnPrimary} w-full justify-center py-3`}
 >
 {savingOnboarding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
 {t('onboarding.save')}
 </motion.button>
 </div>
 )}
 </div>
 )}
 </motion.div>
 </AnimatePresence>
 </div>
 </div>
 );
}

export default ManagerDashboardPage;
